import { CSSProperties } from "@material-ui/core/styles/withStyles";
import { CallMissedOutlined } from "@material-ui/icons";
import { Column } from "components/Table/Table";
import { statusColorPrinter } from "constants/colors";
import useText from "constants/text";
import { differenceInMinutes } from "date-fns";
import {
  OrderStatus,
  Parcel,
  ParcelCashStatus,
  ParcelRecapStatus,
  ParcelStatus,
  Remittance,
  RemittanceBadge,
} from "generated/graphql";
import { OutOfStockIcon, ParcelIcon } from "icons";
import _ from "lodash";
import moment from "moment";
import StatusInfo from "ui/StatusInfo";
import { currencyFormat } from "utils";
import { declineIconeStyle, useRemittanceColumnStyle } from "../style";
import {
  AllCashCollectedIcon,
  CollectedByCashierIcon,
  CollectedByPapserIcon,
  FailedToCollectIcon,
  RemittanceConfirmedIcon,
  RemittanceInHouseIcon,
  RemittanceOngoingIcon,
  YetToCollectIcon,
} from "./icons";

export const checkParcelStatus = [ParcelStatus.ToReceive, ParcelStatus.NotReceived];

export const setParcelStatus = {
  [ParcelStatus.ToReceive]: "TO_COLLECTE",
  [ParcelStatus.NotReceived]: ParcelStatus.NotCollected,
};

export const checkOrderStatus = [OrderStatus.ToReceive, OrderStatus.NotReceived];

export const setOrderStatus = {
  [OrderStatus.ToReceive]: "TO_COLLECTE",
  [OrderStatus.NotReceived]: OrderStatus.NotCollected,
};

export const getCashIcons = (title: string) => {
  const icons = {
    [ParcelRecapStatus?.AllCashCollected]: <AllCashCollectedIcon />,
    [ParcelRecapStatus?.CollectedByPapser]: <CollectedByPapserIcon />,
    [ParcelRecapStatus?.AllCashCollected]: <CollectedByCashierIcon />,
    [ParcelRecapStatus?.RemittanceInHouse]: <RemittanceInHouseIcon />,
    [ParcelRecapStatus?.RemittanceConfirmed]: <RemittanceConfirmedIcon />,
    [ParcelRecapStatus?.YetToCollect]: <YetToCollectIcon />,
    [ParcelRecapStatus?.FailedToCollect]: <FailedToCollectIcon />,
    [ParcelRecapStatus?.RemittanceOnGoing]: <RemittanceOngoingIcon />,
    [ParcelRecapStatus?.NotReceived]: (
      <span style={declineIconeStyle.containerBg as CSSProperties}>
        <CallMissedOutlined style={declineIconeStyle.icon} />
      </span>
    ),
  };
  return icons[title];
};

export const getTextByCashStatus = (status: ParcelRecapStatus | string, text: any, isRmittance?: boolean) => {
  const textStatus = {
    [ParcelRecapStatus?.AllCashCollected]: text.cash_management.all_cash_collected,
    [ParcelRecapStatus?.CollectedByPapser]: text.cash_management.collected_by_papser,
    [ParcelRecapStatus?.AllCashCollected]: text.cash_management.collected_by_cashier,
    [ParcelRecapStatus?.YetToCollect]: text.cash_management.yet_to_collect,
    [ParcelRecapStatus?.FailedToCollect]: text.cash_management.failed_to_collect,
    [ParcelRecapStatus?.RemittanceInHouse]: !isRmittance
      ? text.cash_management.in_remittance
      : text.cash_management.remittance_at_paps,
    [ParcelRecapStatus?.RemittanceOnGoing]: text.cash_management.remittance_ongoing,
    [ParcelRecapStatus?.RemittanceConfirmed]: text.cash_management.remittance_confirmed,
    [ParcelRecapStatus?.NotReceived]: text.cash_management.remittance_declined,
  };

  return textStatus[status];
};

export const getCodeColorByCashStatus = (status: ParcelRecapStatus | string) => {
  const colors = {
    [ParcelRecapStatus?.AllCashCollected]: "#757A93",
    [ParcelRecapStatus?.CollectedByPapser]: "#1F264F",
    [ParcelRecapStatus?.CollectedByCashier]: "#EEC81C",
    [ParcelRecapStatus?.RemittanceInHouse]: "#399EBF",
    [ParcelRecapStatus?.RemittanceConfirmed]: "#2BA815",
    [ParcelRecapStatus?.YetToCollect]: "#F29C50",
    [ParcelRecapStatus?.FailedToCollect]: "#F84949",
    [ParcelRecapStatus?.RemittanceOnGoing]: "#39DD84",
    [ParcelRecapStatus?.NotReceived]: "#F84949",
  };
  return colors[status];
};

export const ParcelCashStatusColors = {
  [ParcelCashStatus?.Confirmed]: "#2BA815",
  [ParcelCashStatus?.InHouse]: "#399EBF",
  [ParcelCashStatus?.OnGoing]: "#39DD84",
  [ParcelCashStatus?.No]: "#F84949",
  [ParcelCashStatus?.Yes]: "#2BA815",
};

const getParcelCashStatus = (status: string): CSSProperties => ({
  background: ParcelCashStatusColors[status],
  color: "#ffff",
  borderRadius: 5,
  padding: "0px 3px",
  fontWeight: "bold",
  width: "120px",
});

const formatParcelCashStatus = (status: string) => status?.replaceAll("_", " ");

export const cardActivitionIdentifier = {
  [ParcelRecapStatus?.CollectedByCashier]: 0,
  [ParcelRecapStatus?.YetToCollect]: 1,
  [ParcelRecapStatus?.FailedToCollect]: 2,
  [ParcelRecapStatus?.RemittanceInHouse]: 3,
  [ParcelRecapStatus?.RemittanceConfirmed]: 4,
};

export const remittanceActiveCardIdentifier = {
  [ParcelRecapStatus?.RemittanceOnGoing]: 0,
  [ParcelRecapStatus?.RemittanceConfirmed]: 1,
  [ParcelRecapStatus?.NotReceived]: 2,
};

export const useParcelRowColumns = (): Column<Partial<Parcel>>[] => {
  const { text } = useText();
  const style = {
    color: "#F2994A",
    fontWeight: "bold",
  };
  return [
    {
      field: "",
      title: "",
      render: ({ isOutOfStock }) => (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          {isOutOfStock ? <OutOfStockIcon width={55} height={25} /> : <ParcelIcon />}
        </div>
      ),
    },
    {
      field: "parcel_UID",
      title: "Code",
    },
    {
      field: "client",
      title: `Client`,
      render: ({ parcel_order }) => {
        const maxLength = 15;
        const fullname = `${parcel_order?.order_receiver?.receiver_firstname} ${parcel_order?.order_receiver?.receiver_lastname}`;
        return <>{fullname.length > maxLength ? `${fullname.substring(0, maxLength - 3)}...` : fullname}</>;
      },
    },
    {
      field: "address",
      title: `${text.all.address}`,
      render: ({ parcel_order }) => {
        const maxLength = 15;
        const address = parcel_order?.order_receiver?.receiver_address?.address;
        return <>{address.length > maxLength ? `${address.substring(0, maxLength - 3)}...` : address}</>;
      },
    },
    {
      field: "parcel_amount_to_collect",
      title: `${text.all.to_collect}`,
      render: ({ parcel_amount_to_collect }) => <span style={style}> {currencyFormat(parcel_amount_to_collect)} </span>,
    },
    {
      field: "cash",
      title: `${text.cash_management.cash_status}`,
      render: ({ parcel_cash_status }) => (
        <span style={getParcelCashStatus(parcel_cash_status)}>{formatParcelCashStatus(parcel_cash_status)}</span>
      ),
    },
    {
      field: "parcel_status",
      title: `${text.all.status}`,
      render: ({ parcel_status }) => {
        const parcelStatus = checkParcelStatus.includes(parcel_status) ? setParcelStatus[parcel_status] : parcel_status;
        return (
          <StatusInfo
            width="127px"
            color={statusColorPrinter({ status: parcelStatus, type: "text" })}
            background={statusColorPrinter({ status: parcelStatus, type: "bg" })}
            border={statusColorPrinter({ status: parcelStatus, type: "border" })}
            status={parcelStatus?.split("_")?.join(" ")}
          />
        );
      },
    },
    {
      field: "parcel_delivery_date",
      title: `${text.all.delivery_date}`,
      render: ({ parcel_delivery_date }) => moment(parcel_delivery_date).format("LL") || "N/A",
    },
    {
      field: "cash_collected",
      title: `${text.cash_management.cash_collected_date}`,
      render: ({ parcel_cash_collection_date }) => moment(parcel_cash_collection_date).format("LL") || "N/A",
    },
    {
      field: "due_date",
      title: `${text.cash_management.remit_due_date}`,
      render: ({ parcel_remittance_due_date }) => moment(parcel_remittance_due_date).format("LL") || "N/A",
    },
    {
      field: "conf_date",
      title: `${text.cash_management.remit_confirm_date}`,
      render: ({ parcel_cash_collection_date }) => moment(parcel_cash_collection_date).format("LL") || "N/A",
    },
  ];
};

export const useModalColumns = (): Column<Partial<Parcel>>[] => {
  const { text } = useText();
  const style = {
    color: "#F2994A",
    fontWeight: "bold",
  };
  return [
    {
      field: "",
      title: "",
      render: ({ isOutOfStock }) => (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          {isOutOfStock ? <OutOfStockIcon width={55} height={25} /> : <ParcelIcon />}
        </div>
      ),
    },
    {
      field: "parcel_UID",
      title: "Code",
    },
    {
      field: "client",
      title: `Client`,
      render: ({ parcel_order }) => {
        const maxLength = 15;
        const fullname = `${parcel_order?.order_receiver?.receiver_firstname} ${parcel_order?.order_receiver?.receiver_lastname}`;
        return <>{fullname.length > maxLength ? `${fullname.substring(0, maxLength - 3)}...` : fullname}</>;
      },
    },
    {
      field: "parcel_amount_to_collect",
      title: `${text.all.to_collect}`,
      render: ({ parcel_amount_to_collect }) => <span style={style}> {currencyFormat(parcel_amount_to_collect)} </span>,
    },
    {
      field: "parcel_status",
      title: `${text.all.status}`,
      render: ({ parcel_status }) => (
        <StatusInfo
          width="127px"
          color={statusColorPrinter({ status: parcel_status, type: "text" })}
          background={statusColorPrinter({ status: parcel_status, type: "bg" })}
          border={statusColorPrinter({ status: parcel_status, type: "border" })}
          status={parcel_status?.split("_")?.join(" ")}
        />
      ),
    },
    {
      field: "cash_collected",
      title: `C. C Date`,
      render: ({ parcel_cash_collection_date }) => moment(parcel_cash_collection_date).format("LL") || "N/A",
    },
    {
      field: "due_date",
      title: `Remit. D Date`,
      render: ({ parcel_remittance_due_date }) => moment(parcel_remittance_due_date).format("LL") || "N/A",
    },
  ];
};

export const isRecent = (createdAt: string) => differenceInMinutes(new Date(), new Date(createdAt)) < 1;

const ids = (parcel: Parcel[]) => parcel?.map((parcel: Parcel) => parcel?._id);

export const usRemittanceColums = (
  text: any,
  onParcelClick: (ParcelIds: string[], remittanceUid: string) => void
): Column<Remittance>[] => {
  const classes = useRemittanceColumnStyle();

  return [
    {
      title: text.cash_management.remittance_id,
      field: "remittanceUID",
      render: ({ remittanceUID }) => remittanceUID,
    },
    {
      title: text.all.amount_to_collect,
      field: "amountToRemit",
      render: ({ amountToRemit }) => <div className={classes.amountToRemit}> {currencyFormat(amountToRemit)} </div>,
    },
    {
      title: "Type",
      field: "type",
      render: ({ type }) => <div>{_.lowerCase(type) || "NA"}</div>,
    },
    {
      title: text.all.creation_date,
      field: "created_at",
      render: ({ createdAt }) => (createdAt ? moment(createdAt).format("LL") : "N/A"),
    },
    {
      title: text.cash_management.confirmation_date,
      field: "confirmCreatedAt",
      render: ({ confirmCreatedAt }) => (confirmCreatedAt ? moment(confirmCreatedAt).format("LL") : "N/A"),
    },
    {
      title: text.cash_management.collect_interval,
      field: "collect_interval",
      render: ({ minDate, maxDate }) => (
        <span>{minDate ? `${moment(minDate).format("LL")}-${moment(maxDate).format("LL")}` : "N/A"}</span>
      ),
    },
    {
      title: `Parcels`,
      field: "parcels",
      render: ({ parcels, remittanceUID }) => (
        <span className={classes.parcelCount} onClick={() => onParcelClick(ids(parcels), remittanceUID)}>
          {" "}
          {`${parcels?.length} ${text.all.parcels}`}{" "}
        </span>
      ),
    },
    {
      title: "",
      field: "badge",
      render: ({ badge }) =>
        badge === RemittanceBadge.NotReceived && <p className={classes.receiveBadge}>{text.all.not_reive}</p>,
    },
  ];
};
